import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { Lock, User, Eye, EyeOff, AtSign, Download, Anchor, Home } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const BOAT_ICON = "https://customer-assets.emergentagent.com/job_recipe-shop-plan/artifacts/n0xjz0mz_E3443421-DE5D-4719-8957-3EE6E82E98C2.png";
const HOME_ICON = "/icons/home-edition-192.png";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Noe gikk galt. Prøv igjen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [identifier, setIdentifier] = useState(() => {
    try { return localStorage.getItem("last_login_identifier") || ""; } catch { return ""; }
  });
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [edition, setEdition] = useState(() => localStorage.getItem("preferred_edition") || "boat");
  const [requestDual, setRequestDual] = useState(false); // register: ask for both accesses
  // Post-login edition picker (shown when the authenticated user has >1 editions_allowed)
  const [pendingPick, setPendingPick] = useState(null); // { user, allowed }
  const [installDialog, setInstallDialog] = useState(null); // { edition: 'boat'|'household' }
  const [installPrompt, setInstallPrompt] = useState(null); // Android beforeinstallprompt event
  const { login, register } = useAuth();
  const navigate = useNavigate();
  // `?next=<url>` — when user came from a join-by-token link, redirect there after login
  const nextUrl = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const n = p.get("next");
      // only allow internal paths (no scheme-absolute URLs) for safety
      return n && n.startsWith("/") ? n : "";
    } catch { return ""; }
  })();

  // Capture Android/Chrome install prompt so we can trigger it manually when user clicks "Last ned"
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Detect device for install-instruction UX
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS/i.test(ua);

  const triggerInstall = async (targetEdition) => {
    // Save preferred edition so the switcher serves the right manifest BEFORE the user adds to homescreen
    localStorage.setItem("preferred_edition", targetEdition);
    // Nudge the manifest switcher by navigating to the edition's start URL in-tab
    // so Safari/Chrome pick up the correct manifest on the next "Add to Home Screen".
    const targetPath = targetEdition === "household" ? "/home?pwa=home" : "/?pwa=boat";
    // Android/Chrome: use captured beforeinstallprompt
    if (installPrompt) {
      // Need to swap manifest first — give React a tick
      const manifestEl = document.querySelector('link[rel="manifest"]');
      if (manifestEl) manifestEl.setAttribute("href", targetEdition === "household" ? "/manifest-home.json" : "/manifest-boat.json");
      await new Promise((r) => setTimeout(r, 150));
      try {
        installPrompt.prompt();
        const choice = await installPrompt.userChoice;
        if (choice.outcome === "accepted") {
          toast.success(targetEdition === "household" ? "Home Edition installeres på hjemmeskjermen" : "Båt-appen installeres på hjemmeskjermen");
          setInstallPrompt(null);
          return;
        }
      } catch (err) {
        console.warn("install prompt failed", err);
        // fall through to instructions dialog
      }
    }
    // iOS or prompt unavailable → open instructions dialog (and pre-navigate so manifest matches)
    if (window.location.pathname + window.location.search !== targetPath) {
      // Open a new tab pointing at the edition so the user can install it directly from there
      window.open(targetPath, "_blank", "noopener");
    }
    setInstallDialog({ edition: targetEdition });
  };

  const selectEdition = (k) => {
    setEdition(k);
    localStorage.setItem("preferred_edition", k);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      let authedUser;
      if (isRegister) {
        if (!username.trim()) { setError("Brukernavn er påkrevd"); setLoading(false); return; }
        if (password.length < 6) { setError("Passord må ha minst 6 tegn"); setLoading(false); return; }
        authedUser = await register("", password, name, username.trim(), edition);
        // If the user asked for dual access, register a pending request now.
        if (requestDual) {
          const other = edition === "boat" ? "household" : "boat";
          try {
            await api.requestEditionAccess(other);
            toast.info(`Forespørsel sendt til admin: tilgang til ${other === "household" ? "Home Edition" : "Båt"} venter på godkjenning`);
          } catch (err) { console.warn("requestEditionAccess failed", err); /* non-fatal */ }
        }
      } else {
        authedUser = await login(identifier, password);
        // Remember last login identifier (username or email) so PC users don't
        // have to type it every visit. Only the IDENTIFIER, never the password.
        try { localStorage.setItem("last_login_identifier", identifier.trim()); } catch { /* ignore */ }
      }
      localStorage.setItem("preferred_edition", edition);
      const allowed = (authedUser && authedUser.editions_allowed) || [edition];
      // Dual-access users: show inline picker before navigating
      if (!isRegister && allowed.length > 1) {
        setPendingPick({ user: authedUser, allowed });
        setLoading(false);
        return;
      }
      // Route to the correct landing page
      if (nextUrl) {
        navigate(nextUrl);
        return;
      }
      if (isRegister) {
        navigate("/boats");
      } else {
        const only = allowed[0] || edition;
        try {
          const res = await api.getActiveBoat();
          const kind = res.data?.kind;
          if (only === "household") {
            navigate(kind === "household" ? "/home" : "/boats");
          } else {
            navigate(kind === "boat" ? "/" : "/boats");
          }
        } catch (err) {
          console.warn("getActiveBoat failed after login", err);
          navigate("/boats");
        }
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  /** User picks which edition to enter (only shown for dual-access accounts). */
  const pickEdition = async (pickedEdition) => {
    if (!pendingPick) return;
    try {
      await api.setPreferredEdition(pickedEdition);
    } catch (err) { console.warn("setPreferredEdition failed", err); /* non-fatal */ }
    localStorage.setItem("preferred_edition", pickedEdition);
    if (nextUrl) {
      navigate(nextUrl);
      return;
    }
    if (pickedEdition === "household") {
      navigate("/home");
    } else {
      try {
        const res = await api.getActiveBoat();
        navigate(res.data?.kind === "boat" ? "/" : "/boats");
      } catch (err) {
        console.warn("getActiveBoat failed for pickEdition", err);
        navigate("/boats");
      }
    }
  };

  const handleGoogleLogin = () => {
    // Persist the user's edition choice so AuthCallback can route them correctly
    // after the Google flow completes.
    localStorage.setItem("preferred_edition", edition);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4 relative overflow-hidden" data-testid="login-page">
      {/* Background logo */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <img
          src="https://customer-assets.emergentagent.com/job_recipe-shop-plan/artifacts/n0xjz0mz_E3443421-DE5D-4719-8957-3EE6E82E98C2.png"
          alt="PantryBuddy"
          className="w-[500px] opacity-[0.05]"
        />
      </div>
      <div className="w-full max-w-sm relative z-10">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-6 mb-4" data-testid="edition-icons">
            <div className="flex flex-col items-center">
              <button
                type="button"
                onClick={() => selectEdition("boat")}
                className={`group transition-all ${edition === "boat" ? "scale-105" : "opacity-75 hover:opacity-100"}`}
                data-testid="edition-boat-btn"
                aria-label="Velg Båt-edition"
              >
                <div className={`h-36 w-36 rounded-[28px] shadow-lg bg-gradient-to-br from-stone-50 to-white border-[3px] p-3 flex items-center justify-center transition-all ${edition === "boat" ? "border-navy-800 ring-4 ring-navy-800/15" : "border-stone-200 hover:border-stone-300"}`}>
                  <img
                    src={BOAT_ICON}
                    alt="PantryBuddy – Båt"
                    className="h-full w-full object-contain rounded-2xl"
                    data-testid="boat-edition-icon"
                  />
                </div>
              </button>
              <span className={`text-[10px] uppercase tracking-[0.18em] mt-2 font-semibold transition-colors ${edition === "boat" ? "text-navy-800" : "text-stone-500"}`}>Båt</span>
              <button
                type="button"
                onClick={() => triggerInstall("boat")}
                className="mt-1 inline-flex items-center gap-1 text-[10px] text-stone-400 hover:text-navy-800 transition-colors"
                data-testid="download-boat-icon"
                title="Installer Båt-appen på telefonen"
              >
                <Download className="h-3 w-3" /> Last ned app
              </button>
            </div>
            <div className="flex flex-col items-center">
              <button
                type="button"
                onClick={() => selectEdition("household")}
                className={`group transition-all ${edition === "household" ? "scale-105" : "opacity-75 hover:opacity-100"}`}
                data-testid="edition-household-btn"
                aria-label="Velg Home Edition"
              >
                <div className={`h-36 w-36 rounded-[28px] shadow-lg bg-gradient-to-br from-stone-50 to-white border-[3px] p-3 flex items-center justify-center transition-all ${edition === "household" ? "border-amber-600 ring-4 ring-amber-600/15" : "border-stone-200 hover:border-stone-300"}`}>
                  <img
                    src={HOME_ICON}
                    alt="PantryBuddy – Home Edition"
                    className="h-full w-full object-contain rounded-2xl"
                    data-testid="home-edition-icon"
                  />
                </div>
              </button>
              <span className={`text-[10px] uppercase tracking-[0.18em] mt-2 font-semibold transition-colors ${edition === "household" ? "text-amber-700" : "text-stone-500"}`}>Home Edition</span>
              <button
                type="button"
                onClick={() => triggerInstall("household")}
                className="mt-1 inline-flex items-center gap-1 text-[10px] text-stone-400 hover:text-amber-700 transition-colors"
                data-testid="download-home-icon"
                title="Installer Home Edition på telefonen"
              >
                <Download className="h-3 w-3" /> Last ned app
              </button>
            </div>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-stone-900 mt-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>PantryBuddy</h1>
          <p className="text-sm text-stone-500 mt-1">
            {isRegister ? "Opprett en ny profil" : "Logg inn for å planlegge måltider"}
          </p>
          <p className="text-[11px] text-stone-400 mt-1" data-testid="edition-hint">
            {isRegister
              ? (edition === "household"
                  ? "Velg ikonet over for å bytte type. Du registreres som Hjemme-bruker."
                  : "Velg ikonet over for å bytte type. Du registreres som Båt-bruker.")
              : (edition === "household"
                  ? "Du blir sendt til Home Edition etter innlogging."
                  : "Du blir sendt til Båt-versjonen etter innlogging.")}
          </p>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm">
          {/* Google login */}
          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 border border-stone-200 rounded-lg px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors mb-4"
            data-testid="google-login-btn"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Logg inn med Google
          </button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-stone-200"></div></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-stone-400">eller</span></div>
          </div>

          {/* Username/password form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            {isRegister && (
              <>
                {/* Valgt edition-banner */}
                <div
                  className={`rounded-lg border-2 px-3 py-2 text-xs flex items-center gap-2 ${edition === "household" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-sailcloth-100 border-navy-200 text-navy-900"}`}
                  data-testid="register-edition-banner"
                >
                  <span className="text-base" aria-hidden>
                    {edition === "household" ? "🏠" : "⚓"}
                  </span>
                  <span className="flex-1">
                    Registrer deg som <strong>{edition === "household" ? "Hjemme-bruker" : "Båt-bruker"}</strong>
                    <br />
                    <span className="text-[10px] opacity-70">Endre ved å klikke ikonet over.</span>
                  </span>
                </div>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Fullt navn" className="w-full rounded-lg border border-stone-200 bg-white pl-10 pr-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="register-name-input" />
                </div>
                <div className="relative">
                  <AtSign className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
                  <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Brukernavn *" required className="w-full rounded-lg border border-stone-200 bg-white pl-10 pr-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="register-username-input" />
                </div>
                <label className="flex items-start gap-2 text-xs text-stone-600 cursor-pointer select-none" data-testid="request-dual-wrapper">
                  <input
                    type="checkbox"
                    checked={requestDual}
                    onChange={(e) => setRequestDual(e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5 rounded border-stone-300"
                    data-testid="request-dual-checkbox"
                  />
                  <span>
                    Jeg trenger tilgang til <strong>begge sider</strong> (Båt og Home).
                    <span className="block text-[10px] text-stone-400">Ekstra tilgang må godkjennes av admin.</span>
                  </span>
                </label>
              </>
            )}
            {!isRegister && (
              <div className="relative">
                <User className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
                <input type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="Brukernavn eller e-post" required autoComplete="username" className="w-full rounded-lg border border-stone-200 bg-white pl-10 pr-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="login-email-input" />
              </div>
            )}
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
              <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Passord" required autoComplete={isRegister ? "new-password" : "current-password"} className="w-full rounded-lg border border-stone-200 bg-white pl-10 pr-10 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="login-password-input" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-2.5 text-stone-400 hover:text-stone-600">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {error && <p className="text-xs text-red-600 bg-red-50 p-2 rounded-lg" data-testid="auth-error">{error}</p>}
            <button type="submit" disabled={loading} className="w-full rounded-lg bg-navy-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors disabled:opacity-50" data-testid="login-form-submit-button">
              {loading ? "Vennligst vent..." : isRegister ? "Opprett profil" : "Logg inn"}
            </button>
          </form>

          <p className="text-center text-xs text-stone-500 mt-4">
            {isRegister ? "Har du allerede en konto?" : "Har du ikke en konto?"}{" "}
            <button onClick={() => { setIsRegister(!isRegister); setError(""); }} className="text-navy-800 font-medium hover:underline" data-testid="toggle-auth-mode">
              {isRegister ? "Logg inn" : "Opprett konto"}
            </button>
          </p>
        </div>

        <div className="text-center mt-6">
          <a href="mailto:admin@pantrybuddy.no" className="text-xs text-stone-400 hover:text-navy-800 transition-colors" data-testid="contact-email">
            admin@pantrybuddy.no
          </a>
        </div>
      </div>

      {/* Post-login edition picker — dual-access users get to choose which side to enter */}
      {pendingPick && (
        <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="edition-picker-overlay">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Hvor vil du inn i dag?
              </h2>
              <p className="text-sm text-stone-500 mt-1">Du har tilgang til begge sider. Velg hvilken du vil bruke nå.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => pickEdition("boat")}
                className="rounded-xl border-2 border-navy-200 hover:border-navy-600 bg-sailcloth-100/50 hover:bg-sailcloth-100 p-5 text-left transition-all group"
                data-testid="pick-edition-boat"
              >
                <Anchor className="h-8 w-8 text-navy-700 mb-2 group-hover:scale-110 transition-transform" />
                <div className="text-base font-bold text-navy-900">Båt</div>
                <div className="text-[11px] text-stone-500 mt-1">Skift, turer, maritim UI</div>
              </button>
              <button
                onClick={() => pickEdition("household")}
                className="rounded-xl border-2 border-amber-200 hover:border-amber-500 bg-amber-50/50 hover:bg-amber-50 p-5 text-left transition-all group"
                data-testid="pick-edition-household"
              >
                <Home className="h-8 w-8 text-amber-700 mb-2 group-hover:scale-110 transition-transform" />
                <div className="text-base font-bold text-amber-900">Home Edition</div>
                <div className="text-[11px] text-stone-500 mt-1">Hjem, husstand, budsjett</div>
              </button>
            </div>
            <p className="text-[10px] text-stone-400 text-center">Du kan bytte når som helst fra menyen etterpå.</p>
          </div>
        </div>
      )}

      {/* Install instructions dialog (iOS mainly; fallback for Android) */}
      <Dialog open={!!installDialog} onOpenChange={(o) => !o && setInstallDialog(null)}>
        <DialogContent className="max-w-md" data-testid="install-instructions-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Installer {installDialog?.edition === "household" ? "Home Edition" : "Båt-appen"} på telefonen
            </DialogTitle>
            <DialogDescription>
              {isIOS
                ? "På iPhone/iPad: åpne menyen i Safari og velg «Legg til på hjemskjerm»."
                : "Åpne menyen i nettleseren og velg «Installer app» eller «Legg til på hjemskjerm»."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-md border border-stone-200 bg-stone-50 p-3">
              <img
                src={installDialog?.edition === "household" ? HOME_ICON : BOAT_ICON}
                alt=""
                className="h-14 w-14 rounded-xl border border-stone-200 bg-white object-contain p-1"
              />
              <div>
                <div className="text-sm font-semibold text-stone-900">
                  {installDialog?.edition === "household" ? "PantryBuddy Hjem" : "PantryBuddy Båt"}
                </div>
                <div className="text-xs text-stone-500 mt-0.5">
                  {installDialog?.edition === "household"
                    ? "Åpner på hjem-siden når du trykker på ikonet"
                    : "Åpner på skift-/turplan når du trykker på ikonet"}
                </div>
              </div>
            </div>
            {isIOS ? (
              <ol className="space-y-2 text-sm text-stone-700 list-decimal pl-5">
                <li>Trykk på <strong>Del</strong>-ikonet nederst i Safari (firkant med pil opp).</li>
                <li>Bla ned og velg <strong>«Legg til på hjem-skjerm»</strong>.</li>
                <li>Trykk <strong>Legg til</strong> øverst til høyre.</li>
                <li>Ikonet dukker opp på hjemskjermen – trykk for å åpne som egen app.</li>
              </ol>
            ) : (
              <ol className="space-y-2 text-sm text-stone-700 list-decimal pl-5">
                <li>Trykk på menyen (⋮) øverst til høyre i nettleseren.</li>
                <li>Velg <strong>«Installer app»</strong> eller <strong>«Legg til på hjemskjerm»</strong>.</li>
                <li>Bekreft – ikonet legges til hjemskjermen som en egen app.</li>
              </ol>
            )}
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
              💡 <strong>Vil du ha begge apper?</strong> Installer først den ene, deretter gå tilbake til login-siden, velg det andre ikonet, og trykk «Last ned app» på nytt. Du får da to separate app-ikoner på hjemskjermen.
            </div>
            <button
              onClick={() => setInstallDialog(null)}
              className="w-full rounded-md bg-stone-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-stone-800 transition-colors"
              data-testid="close-install-dialog"
            >
              Skjønner, takk
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
